// Notes editor — CodeMirror 6 with markdown grammar.
//
// Same external contract as the Phase 2a textarea editor so the
// parent didn't have to change: `initialContent` mounts the document
// and stays stable through edits; `onSave` fires ~700ms after the
// last keystroke; `onDirty` fires every change; paste / drop of an
// image goes through `uploadAsset` and gets spliced as `![](relPath)`
// at the caret.
//
// The CM6 view is created once per mount. We re-key in the parent on
// activePath change so swapping notes destroys and re-creates the
// view (cheaper than rebuilding state via a transaction since the
// view is already small).

import { useEffect, useRef, useState } from "preact/hooks";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLine, drawSelection, dropCursor } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { bracketMatching, defaultHighlightStyle, syntaxHighlighting, indentOnInput, foldGutter, foldKeymap } from "@codemirror/language";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import type { NotesSaveState } from "../../lib/use-notes";

interface NotesEditorProps {
  initialContent: string;
  onSave: (content: string) => void;
  onDirty: () => void;
  /** Optional — fires on every change so the parent can mirror into a
   *  preview pane. The split-view feature uses this. */
  onChange?: (content: string) => void;
  uploadAsset: (dataUrl: string) => Promise<{ ok: boolean; relPath?: string; error?: string }>;
  saveState: NotesSaveState;
  notify: (msg: string) => void;
}

const DEBOUNCE_MS = 700;

export function NotesEditor({
  initialContent, onSave, onDirty, onChange, uploadAsset, saveState: _saveState, notify,
}: NotesEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const timerRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  // Latest callbacks — captured into closures the CM listener can call
  // without re-creating the view on every render.
  const cbRef = useRef({ onSave, onDirty, onChange, uploadAsset, notify });
  cbRef.current = { onSave, onDirty, onChange, uploadAsset, notify };
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scheduleSave = (next: string) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      dirtyRef.current = true;
      cbRef.current.onDirty();
      timerRef.current = window.setTimeout(() => {
        cbRef.current.onSave(next);
        dirtyRef.current = false;
        timerRef.current = null;
      }, DEBOUNCE_MS);
    };

    const handleImageFile = async (file: File) => {
      if (!file.type.startsWith("image/")) {
        cbRef.current.notify("Only images can be pasted into a note.");
        return;
      }
      const reader = new FileReader();
      let dataUrl: string;
      try {
        dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
      } catch {
        cbRef.current.notify("Couldn't read the image.");
        return;
      }
      const r = await cbRef.current.uploadAsset(dataUrl);
      if (!r.ok || !r.relPath) { cbRef.current.notify(r.error || "Upload failed"); return; }
      const v = viewRef.current;
      if (!v) return;
      const insert = `![](${r.relPath})\n`;
      const sel = v.state.selection.main;
      v.dispatch({
        changes: { from: sel.from, to: sel.to, insert },
        selection: { anchor: sel.from + insert.length },
      });
      v.focus();
    };

    const pasteHandler = EditorView.domEventHandlers({
      paste: (event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.kind === "file" && item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageFile(file);
              return true;
            }
          }
        }
        return false;
      },
      drop: (event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        let handled = false;
        for (const f of Array.from(files)) {
          if (f.type.startsWith("image/")) { handled = true; handleImageFile(f); }
        }
        if (handled) event.preventDefault();
        return handled;
      },
    });

    const updateListener = EditorView.updateListener.of((upd) => {
      if (!upd.docChanged) return;
      const value = upd.state.doc.toString();
      cbRef.current.onChange?.(value);
      scheduleSave(value);
    });

    const extensions: Extension[] = [
      lineNumbers(),
      foldGutter(),
      history(),
      drawSelection(),
      dropCursor(),
      indentOnInput(),
      bracketMatching(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      markdown({ base: markdownLanguage, codeLanguages: [] }),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
        indentWithTab,
      ]),
      EditorView.lineWrapping,
      pasteHandler,
      updateListener,
      themeExt,
    ];

    const state = EditorState.create({ doc: initialContent, extensions });
    const view = new EditorView({ state, parent: host });
    viewRef.current = view;
    setReady(true);
    view.focus();

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        if (dirtyRef.current) {
          // Flush the buffered edit so picking another note doesn't
          // lose what was typed in the final 700ms.
          cbRef.current.onSave(view.state.doc.toString());
        }
      }
      view.destroy();
      viewRef.current = null;
    };
    // initialContent is intentionally NOT a dep — the parent re-keys
    // this component on note swap, which unmounts/remounts. Including
    // it would rebuild the editor on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={hostRef} className="notes-editor-cm" data-ready={ready} />;
}

// Subtle theme that inherits our shell tokens. We deliberately keep
// the highlight style light so syntax doesn't drown out body prose.
const themeExt = EditorView.theme({
  "&": {
    height: "100%",
    minHeight: "360px",
    fontFamily: "var(--f-mono)",
    fontSize: "13.5px",
    background: "transparent",
    color: "var(--text)",
  },
  ".cm-scroller": {
    fontFamily: "var(--f-mono)",
    lineHeight: "1.6",
    padding: "8px 4px 80px",
  },
  ".cm-content": {
    caretColor: "var(--accent)",
    padding: "0 4px",
  },
  "&.cm-focused": {
    outline: "none",
  },
  ".cm-gutters": {
    background: "transparent",
    border: "0",
    color: "var(--text-dim)",
    fontSize: "11px",
  },
  ".cm-activeLineGutter, .cm-activeLine": {
    background: "color-mix(in oklch, var(--accent) 6%, transparent)",
  },
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: "var(--accent)",
  },
  ".cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection": {
    background: "color-mix(in oklch, var(--accent) 24%, transparent) !important",
  },
  ".cm-link, .cm-url": { color: "var(--accent)" },
  ".cm-heading": { color: "var(--text)", fontWeight: "600" },
  ".cm-quote": { color: "var(--text-mute)", fontStyle: "italic" },
  ".cm-strong": { color: "var(--text)", fontWeight: "600" },
  ".cm-emphasis": { fontStyle: "italic" },
  ".cm-monospace, .cm-formatting-code, .cm-codeblock, .cm-inline-code": {
    background: "color-mix(in oklch, var(--bg-elev) 60%, transparent)",
    borderRadius: "4px",
    padding: "0 3px",
  },
});
